import { useMemo } from "react";
import { useField } from "formik";
import { Flex, Text } from "@jsq/ds/core";
import { FormSelect, FormSwitch } from "@jsq/ds/forms";
import {
  CURRENCY_SYMBOL_POSITION_OPTIONS,
  NEGATIVE_NUMBERS_OPTIONS,
  NULL_DISPLAY_OPTIONS,
  NUMBER_SCALE_OPTIONS,
  ZERO_DISPLAY_OPTIONS,
} from "./constants";
import { DataFormatRow } from "./DataFormatRow";
import { DateFormatBuilderFields } from "./DateFormatBuilderFields";
import {
  formatDatePreview,
  formatNullPreview,
  formatNumberPreview,
  formatZeroPreview,
} from "./formatPreview";
import { renderFormSelectOptions } from "./FormSelectOptions";
import { buildPattern, DEFAULT_BUILDER_STATE, parsePattern } from "./patternBuilder";
import * as styles from "./styles_vanilla.css";
import type {
  CurrencySymbolPositionOption,
  NegativeNumbersOption,
  NullDisplayOption,
  NumberScaleOption,
  ZeroDisplayOption,
} from "./types";
import type { FieldDataType } from "~/graphql/supergraphTypes";

const joinPath = (prefix: string, name: string) => (prefix ? `${prefix}.${name}` : name);

// Each preview subscribes only to the field(s) it renders so unrelated
// keystrokes (e.g. typing in the Query name input) don't re-render the
// preview tree.
const NumberPreview = ({
  valuesPath,
  fieldType,
}: {
  valuesPath: string;
  fieldType?: FieldDataType;
}) => {
  const [{ value: negativeNumbers }] = useField<NegativeNumbersOption>(
    joinPath(valuesPath, "negativeNumbers"),
  );
  const [{ value: numberScale }] = useField<NumberScaleOption>(
    joinPath(valuesPath, "numberScale"),
  );
  const [{ value: thousandsSeparator }] = useField<boolean>(
    joinPath(valuesPath, "thousandsSeparator"),
  );
  const [{ value: showCurrencySymbol }] = useField<boolean>(
    joinPath(valuesPath, "showCurrencySymbol"),
  );
  const [{ value: currencySymbolPosition }] = useField<CurrencySymbolPositionOption>(
    joinPath(valuesPath, "currencySymbolPosition"),
  );
  const [{ value: decimalPlaces }] = useField<number>(joinPath(valuesPath, "decimalPlaces"));

  return (
    <Text variant="secondary-md">
      {formatNumberPreview({
        negativeNumbers,
        numberScale:
          fieldType === undefined || fieldType === "QUANTITY" || fieldType === "VALUE"
            ? numberScale
            : "as_is",
        thousandsSeparator,
        showCurrencySymbol:
          fieldType === undefined || fieldType === "VALUE" ? showCurrencySymbol : false,
        currencySymbolPosition,
        decimalPlaces,
      })}
    </Text>
  );
};

const ZeroPreview = ({ valuesPath }: { valuesPath: string }) => {
  const [{ value }] = useField<ZeroDisplayOption>(joinPath(valuesPath, "zeroDisplay"));
  return <Text variant="secondary-md">{formatZeroPreview(value)}</Text>;
};

const NullPreview = ({ valuesPath }: { valuesPath: string }) => {
  const [{ value }] = useField<NullDisplayOption>(joinPath(valuesPath, "nullDisplay"));
  return <Text variant="secondary-md">{formatNullPreview(value)}</Text>;
};

const DatePreview = ({ valuesPath }: { valuesPath: string }) => {
  const [{ value }] = useField<string>(joinPath(valuesPath, "dateFormatPattern"));
  return <Text variant="secondary-md">{formatDatePreview(value)}</Text>;
};

/** Formik adapter for the composable date-format builder: parses the
 * stored `dateFormatPattern` into builder state (unparseable patterns
 * seed the default) and serializes every edit back to the pattern. */
const DateFormatBuilder = ({
  valuesPath,
  isReadOnly,
}: {
  valuesPath: string;
  isReadOnly: boolean;
}) => {
  const [{ value }, , { setValue }] = useField<string>(
    joinPath(valuesPath, "dateFormatPattern"),
  );
  const state = useMemo(() => parsePattern(value) ?? DEFAULT_BUILDER_STATE, [value]);
  return (
    <DateFormatBuilderFields
      state={state}
      onChange={(next) => setValue(buildPattern(next))}
      disabled={isReadOnly}
      idPrefix={joinPath(valuesPath, "on")}
    />
  );
};

type OnStateProps = {
  valuesPath: string;
  /** When provided, limits visible fields to those relevant for this field type.
   * When undefined (query-level), all fields are shown. */
  fieldType?: FieldDataType;
  isReadOnly?: boolean;
};

const isNumericType = (fieldType: FieldDataType | undefined): boolean =>
  fieldType === undefined ||
  fieldType === "QUANTITY" ||
  fieldType === "VALUE" ||
  fieldType === "PERCENTAGE";

export const OnState = ({ valuesPath, fieldType, isReadOnly = false }: OnStateProps) => {
  const showCurrencyFields = fieldType === undefined || fieldType === "VALUE";
  const showNumberScale =
    fieldType === undefined || fieldType === "QUANTITY" || fieldType === "VALUE";
  const showNumericFormatFields = isNumericType(fieldType);
  const showDateFormat = fieldType === undefined || fieldType === "DATE";

  // Read `showCurrencySymbol` directly to gate the position select.
  const [{ value: showCurrencySymbol }] = useField<boolean>(
    joinPath(valuesPath, "showCurrencySymbol"),
  );

  const nullDisplayRow = (
    <DataFormatRow
      field={
        <FormSelect
          name={joinPath(valuesPath, "nullDisplay")}
          label="Null display"
          className={styles.formControl}
          data-testid="data-format-null-display"
          disabled={isReadOnly}
        >
          {renderFormSelectOptions(NULL_DISPLAY_OPTIONS)}
        </FormSelect>
      }
      preview={<NullPreview valuesPath={valuesPath} />}
    />
  );

  const dateFormatRow = showDateFormat ? (
    <DataFormatRow
      field={<DateFormatBuilder valuesPath={valuesPath} isReadOnly={isReadOnly} />}
      preview={<DatePreview valuesPath={valuesPath} />}
    />
  ) : null;

  if (!showNumericFormatFields) {
    // TEXT or DATE: null display, then optional date format
    return (
      <Flex flexDirection="column">
        {nullDisplayRow}
        {dateFormatRow}
      </Flex>
    );
  }

  // Numeric types (NUMBER, CURRENCY, PERCENTAGE) or query-level (all fields)
  return (
    <Flex flexDirection="column">
      <DataFormatRow
        field={
          <>
            <FormSelect
              name={joinPath(valuesPath, "negativeNumbers")}
              label="Negative numbers"
              className={styles.formControl}
              data-testid="data-format-negative-numbers"
              disabled={isReadOnly}
            >
              {renderFormSelectOptions(NEGATIVE_NUMBERS_OPTIONS)}
            </FormSelect>
            {showNumberScale && (
              <FormSelect
                name={joinPath(valuesPath, "numberScale")}
                label="Number scale"
                className={styles.formControl}
                data-testid="data-format-number-scale"
                disabled={isReadOnly}
              >
                {renderFormSelectOptions(NUMBER_SCALE_OPTIONS)}
              </FormSelect>
            )}
            <FormSwitch
              name={joinPath(valuesPath, "thousandsSeparator")}
              id={joinPath(valuesPath, "data-format-thousands-separator")}
              label="1,000 separators"
              size="small"
              disabled={isReadOnly}
            />
            {showCurrencyFields && (
              <>
                <FormSwitch
                  name={joinPath(valuesPath, "showCurrencySymbol")}
                  id={joinPath(valuesPath, "data-format-show-currency-symbol")}
                  label="Show currency symbol"
                  size="small"
                  disabled={isReadOnly}
                />
                {showCurrencySymbol && (
                  <FormSelect
                    name={joinPath(valuesPath, "currencySymbolPosition")}
                    label="Currency symbol position"
                    className={styles.formControl}
                    data-testid="data-format-currency-position"
                    disabled={isReadOnly}
                  >
                    {renderFormSelectOptions(CURRENCY_SYMBOL_POSITION_OPTIONS)}
                  </FormSelect>
                )}
              </>
            )}
          </>
        }
        previewLabel={<Text variant="secondary-md">Display preview</Text>}
        preview={<NumberPreview valuesPath={valuesPath} fieldType={fieldType} />}
      />
      <DataFormatRow
        field={
          <FormSelect
            name={joinPath(valuesPath, "zeroDisplay")}
            label="Zero display"
            className={styles.formControl}
            data-testid="data-format-zero-display"
            disabled={isReadOnly}
          >
            {renderFormSelectOptions(ZERO_DISPLAY_OPTIONS)}
          </FormSelect>
        }
        preview={<ZeroPreview valuesPath={valuesPath} />}
      />
      {nullDisplayRow}
      {dateFormatRow}
    </Flex>
  );
};
